import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24" data-testid="terms-conditions-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 py-16 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C6A87C] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#C6A87C]/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-[#C6A87C]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-1">Legal</p>
              <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-gray-900">
                Terms and Conditions
              </h1>
            </div>
          </div>
          <p className="text-gray-500">
            Last updated: December 2025 | Effective Date: December 1, 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-12 py-16">
        <div className="max-w-4xl mx-auto space-y-12" style={{ color: '#333333' }}>

          {/* 1. Introduction */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              1. Introduction
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                Welcome to My Gear Rentals ("Company," "we," "us," or "our"). These Terms and Conditions ("Terms") govern 
                your access to and use of our motorcycle gear rental services, website, and related offerings. By placing a 
                rental order or using our services, you ("Renter," "Customer," or "you") agree to be legally bound by these Terms.
              </p>
              <p>
                My Gear Rentals provides premium motorcycle protective equipment on a short-term rental basis, including 
                helmets, jackets, gloves, and complete kits. Our service is designed for riders who need high-quality gear 
                for trips, track days, or temporary use without the commitment of purchasing.
              </p>
            </div>
          </div>

          {/* 2. Rental Agreement */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              2. Rental Agreement & Eligibility
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                By completing a rental order, you enter into a binding rental agreement with My Gear Rentals. You must be at 
                least 18 years of age and possess a valid government-issued photo ID to rent equipment from us.
              </p>
              <p><strong className="text-gray-900">By renting, you agree to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate personal information and a valid payment method</li>
                <li>Use the gear solely for its intended purpose (motorcycle riding protection)</li>
                <li>Not modify, alter, disassemble, or tamper with any rented equipment</li>
                <li>Not sublease, lend, or transfer the gear to any third party</li>
                <li>Return all gear, including carrying bags, accessories, and return shipping labels provided</li>
                <li>Report any damage, defects, or issues within 24 hours of discovery</li>
              </ul>
              <p>
                <strong className="text-gray-900">ID Verification:</strong> We reserve the right to request a copy of your 
                government-issued photo ID before processing your order. This is a standard security measure to protect against 
                fraud and ensure accountability.
              </p>
            </div>
          </div>

          {/* 3. Rental Period & Pricing */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              3. Rental Period & Pricing
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                All rentals require a <strong className="text-gray-900">minimum 3-day rental period</strong>. Daily rates vary 
                by product tier:
              </p>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded">
                <ul className="space-y-2">
                  <li><strong className="text-gray-900">Pro-Series Kits:</strong> $99.99/day</li>
                  <li><strong className="text-gray-900">Standard Kits:</strong> $59.99/day</li>
                </ul>
              </div>
              <p>
                Duration discounts may apply automatically: 15% off for 3-6 day rentals, 25% off for 7+ day rentals. Discounts 
                are applied at checkout and may change without prior notice.
              </p>
              <p>
                Your rental period begins on the selected start date and ends at 11:59 PM on the selected end date. You are 
                responsible for shipping the gear back by the return-by date shown in your order confirmation (typically 2 days 
                after the rental end date to account for transit).
              </p>
            </div>
          </div>

          {/* 4. Security Deposit */}
          <div className="bg-green-50 border border-green-200 p-8 rounded" data-testid="deposit-terms-section">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              4. Refundable Security Deposit
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                A <strong className="text-gray-900">$150 Refundable Security Deposit</strong> is required for all rental orders. 
                This deposit is collected at checkout and serves as collateral against potential damage, loss, or unreturned gear.
              </p>
              <p><strong className="text-gray-900">Deposit Terms:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The deposit is charged to your payment method at the time of checkout</li>
                <li>The full deposit is refunded within 5-7 business days after we receive and inspect your returned gear</li>
                <li>If gear is returned damaged, repair or replacement costs will be deducted from the deposit</li>
                <li>If gear is not returned within 7 days of the return-by date, the deposit is forfeited and additional replacement charges may apply</li>
                <li>Normal wear and tear (minor scuffs, light soiling) will not result in any deposit deduction</li>
              </ul>
            </div>
          </div>

          {/* 5. Equipment Damage & Loss */}
          <div className="bg-red-50 border border-red-200 p-8 rounded" data-testid="damage-terms-section">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              5. Equipment Damage & Loss Liability
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                You are solely responsible for all rented equipment from the moment of delivery until it is received back at 
                our facility in satisfactory condition.
              </p>
              <p><strong className="text-gray-900">Damage Categories:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Normal Wear:</strong> Minor scuffs, light dust, and general signs of use from normal motorcycle riding. No charges apply.</li>
                <li><strong className="text-gray-900">Minor Damage:</strong> Broken zippers, torn liners, scratched visors, minor abrasions. Repair costs ($25-$150) deducted from deposit.</li>
                <li><strong className="text-gray-900">Major Damage:</strong> Structural damage to helmets, deep tears in leather, cracked armor, water damage. Replacement cost charged.</li>
                <li><strong className="text-gray-900">Total Loss:</strong> Gear involved in a crash, stolen, or not returned. Full retail replacement cost charged.</li>
              </ul>
              <div className="bg-white border border-gray-200 p-4 mt-4 rounded">
                <p className="font-semibold text-gray-900 mb-3">Typical Replacement Costs:</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Premium Helmets</p>
                    <p className="text-gray-900 font-semibold">$800 - $1,800</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Riding Jackets</p>
                    <p className="text-gray-900 font-semibold">$500 - $900</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Riding Gloves</p>
                    <p className="text-gray-900 font-semibold">$200 - $500</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Complete Kits</p>
                    <p className="text-gray-900 font-semibold">$1,500 - $3,200</p>
                  </div>
                </div>
              </div>
              <p>
                <strong className="text-gray-900">Optional Damage Protection:</strong> We offer optional Damage Protection 
                insurance at $25/day per item. This covers minor scuffs, broken zippers/fasteners, theft (with police report), 
                and professional cleaning. It does not cover total loss from crashes or damage due to negligence.
              </p>
            </div>
          </div>

          {/* 6. Shipping & Delivery */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              6. Shipping, Hotel Delivery & Return Policy
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                My Gear Rentals offers <strong className="text-gray-900">Direct-to-Hotel and Airbnb delivery</strong> across 
                the continental United States. We ship your gear directly to your hotel, resort, vacation rental, or residence 
                so it is waiting for you upon arrival.
              </p>
              <p><strong className="text-gray-900">Shipping Tiers:</strong></p>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded">
                <ul className="space-y-2">
                  <li><strong className="text-gray-900">Standard Shipping (FREE):</strong> Orders placed 7+ days before the rental start date. Gear arrives 1 day before your rental begins.</li>
                  <li><strong className="text-gray-900">Expedited Shipping ($50):</strong> Orders placed 3-6 days before the rental start date. Gear arrives 1 day before your rental begins.</li>
                  <li>Orders placed fewer than 3 days before the rental start date cannot be accommodated.</li>
                </ul>
              </div>
              <p><strong className="text-gray-900">Hotel/Airbnb Delivery Instructions:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use your full name and reservation/booking name as the recipient</li>
                <li>Include the hotel name, full street address, and room number (if known)</li>
                <li>For Airbnb/vacation rentals, provide the exact property address and any access codes or instructions</li>
                <li>Contact the hotel/host in advance to confirm they can receive packages on your behalf</li>
              </ul>
              <p><strong className="text-gray-900">Return Shipping:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>A prepaid return shipping label is included with every order</li>
                <li>Pack all gear in the provided carrying bag/box</li>
                <li>Drop off at any UPS or FedEx location by the return-by date</li>
                <li>Keep the tracking receipt as proof of return shipment</li>
              </ul>
              <p>
                My Gear Rentals is not liable for packages lost, stolen, or misdelivered by shipping carriers, hotels, or 
                property managers. We strongly recommend tracking all shipments and confirming delivery with your accommodation provider.
              </p>
            </div>
          </div>

          {/* 7. Late Returns */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              7. Late Returns & Extensions
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                All rentals include a <strong className="text-gray-900">2-day buffer period</strong> after the rental end date 
                for return shipping and processing.
              </p>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded">
                <p className="font-semibold text-gray-900 mb-2">Late Return Charges:</p>
                <ul className="space-y-2">
                  <li><strong className="text-[#C6A87C]">1.5x Daily Rate</strong> per day for each day past the return-by date</li>
                  <li>Late fees are charged automatically to the card on file</li>
                  <li>After 7 days late, gear is considered lost and full replacement costs apply</li>
                </ul>
              </div>
              <p>
                <strong className="text-gray-900">Extensions:</strong> If you need to extend your rental, contact us before 
                your return-by date. Extensions are subject to availability and charged at the standard daily rate.
              </p>
              <p>
                <strong className="text-gray-900">Grace Period:</strong> If your return is delayed due to carrier issues 
                (with tracking proof), contact support within 24 hours for a potential fee waiver.
              </p>
            </div>
          </div>

          {/* 8. Assumption of Risk */}
          <div className="bg-yellow-50 border border-yellow-200 p-8 rounded" data-testid="risk-terms-section">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              8. Assumption of Risk & Liability Waiver
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                <strong className="text-gray-900">IMPORTANT: READ CAREFULLY BEFORE RENTING.</strong> Motorcycle riding is an 
                inherently dangerous activity. By renting protective gear from My Gear Rentals, you acknowledge and voluntarily 
                accept all risks associated with motorcycle riding, including but not limited to: accidents, collisions, 
                equipment malfunction, weather conditions, road hazards, and the actions of other motorists.
              </p>
              <p>
                <strong className="text-gray-900">No Guarantee of Protection:</strong> While all our gear meets or exceeds 
                applicable CE/DOT safety certifications, no protective equipment can guarantee prevention of injury in all 
                circumstances. Gear performance depends on specific impact conditions, proper fit, and correct use.
              </p>
              <p>
                <strong className="text-gray-900">Limitation of Liability:</strong> To the maximum extent permitted by law, 
                My Gear Rentals shall not be liable for any direct, indirect, incidental, special, consequential, or punitive 
                damages arising from: your use of rented gear; any injury, death, or property damage while wearing or using the 
                gear; equipment failure; or improper fit or use.
              </p>
              <p>
                <strong className="text-gray-900">Indemnification:</strong> You agree to indemnify, defend, and hold harmless 
                My Gear Rentals, its owners, officers, employees, and affiliates from any claims, damages, losses, or expenses 
                arising from your use of the service or violation of these Terms.
              </p>
            </div>
          </div>

          {/* 9. Cancellation & Refunds */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              9. Cancellation & Refunds
            </h2>
            <div className="text-gray-600 space-y-4">
              <p><strong className="text-gray-900">Before Shipment:</strong> Full refund if cancelled before gear ships.</p>
              <p><strong className="text-gray-900">After Shipment:</strong> No refund for the rental period. You may return gear early but rental fees are not prorated.</p>
              <p><strong className="text-gray-900">Size Exchanges:</strong> One free size exchange within 24 hours of delivery. Additional exchanges may incur shipping charges.</p>
              <p><strong className="text-gray-900">Security Deposit:</strong> Refunded in full within 5-7 business days of satisfactory gear return, minus any applicable damage charges.</p>
            </div>
          </div>

          {/* 10. Privacy */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              10. Privacy & Data
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                Your personal information is collected and used solely for the purpose of processing rentals and improving 
                our service. We do not sell your personal data to third parties. Information collected includes your name, 
                email, shipping address, and payment details.
              </p>
            </div>
          </div>

          {/* 11. Governing Law */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              11. Governing Law & Disputes
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>
                These Terms are governed by the laws of the State of New York, without regard to conflict of law principles. 
                Any disputes arising from these Terms or your use of our services shall be resolved through binding arbitration 
                in New York, New York, unless you opt out within 30 days of your first rental.
              </p>
            </div>
          </div>

          {/* 12. Contact */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              12. Contact Information
            </h2>
            <div className="text-gray-600 space-y-4">
              <p>For questions about these Terms, contact us at:</p>
              <p>
                <strong className="text-gray-900">My Gear Rentals</strong><br />
                Email: legal@mygearrentals.com<br />
                Support: support@mygearrentals.com
              </p>
            </div>
          </div>

          {/* Acceptance Notice */}
          <div className="p-6 bg-[#C6A87C]/10 border border-[#C6A87C]/30 rounded">
            <p className="text-gray-900 font-semibold">
              By completing a rental order, you acknowledge that you have read, understood, and agree to be bound by all 
              terms and conditions stated herein, including the Equipment Damage & Loss Liability, Security Deposit Policy, 
              Hotel Delivery & Return Policy, and Assumption of Risk & Liability Waiver.
            </p>
          </div>

          {/* Navigation */}
          <div className="mt-12 flex gap-4 justify-center">
            <Link to="/terms">
              <Button 
                variant="outline"
                className="border-gray-300 hover:border-[#C6A87C] hover:text-[#C6A87C] text-gray-700 uppercase tracking-wider"
              >
                View Terms of Service
              </Button>
            </Link>
            <Link to="/faq">
              <Button 
                variant="outline"
                className="border-gray-300 hover:border-[#C6A87C] hover:text-[#C6A87C] text-gray-700 uppercase tracking-wider"
              >
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;
